import React from "react";
import { useSearchParams } from "react-router-dom";
import { DASHBOARD_BASE } from "../../../../config/featureFlags";
import { rangeForPreset } from "../../components/DateRange";

/**
 * Where the drill-down lives, and how the dashboard's filters travel with it.
 *
 * The drill-down used to be a drawer: state in a component, invisible to the
 * URL. As pages it has to carry its own context, because the reader can now
 * refresh, deep-link, or arrive from a pasted link — and a detail page that
 * silently reset to "today" would show different numbers than the dashboard
 * the reader clicked from. So the window and the member scope ride in the
 * query string, and every link the dashboard emits stamps them.
 *
 * Round-trip is exact: rangeForPreset() normalises to 00:00:00.000 →
 * 23:59:59.999 local, which is what start/end dates alone reconstruct.
 */

export const CLASSIC_BASE = `${DASHBOARD_BASE}/classic`;
export const GRAPHICAL_BASE = `${DASHBOARD_BASE}/graphical`;

/**
 * drillBase — which dashboard this drill belongs to, from the live URL.
 *
 * A person/project/tool page is the same component whether it was opened from
 * the classic card dashboard or the graphical one. What differs is where "back"
 * and the sub-drill links should point. Rather than thread a base argument
 * through every builder and call site, we read it from the pathname.
 *
 * The graphical dashboard is reachable at TWO paths: /graphical, and the
 * dashboard root itself (it is the index route). Both must drill back into the
 * graphical world — matching only "/graphical" sent anyone who started from the
 * root back to the card grid instead of where they came from.
 */
export function drillBase() {
  if (typeof window === "undefined") return CLASSIC_BASE;
  const path = window.location.pathname.replace(/\/+$/, "");
  if (path.includes("/graphical")) return GRAPHICAL_BASE;
  if (path === DASHBOARD_BASE) return GRAPHICAL_BASE; // index route = graphical
  return CLASSIC_BASE;
}

const pad = (n) => String(n).padStart(2, "0");
export const fmtDay = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

/** The dashboard's current filters as a query string, for any drill-down link. */
export function filterQuery(range, member) {
  const p = new URLSearchParams();
  if (range?.start) p.set("start", fmtDay(range.start));
  if (range?.end) p.set("end", fmtDay(range.end));
  // The preset key is carried so the dashboard's chip row lights up correctly
  // on the way back — dates alone cannot tell "7 days" from a custom span.
  if (range?.key) p.set("preset", range.key);
  if (member) p.set("member", String(member));
  /* Sample-data mode is a property of the SESSION, not of one page: if the
     reader turned it on to check the layout, every link they follow has to keep
     it on, or the trail silently flips back to real data halfway down. Read off
     the live URL so no call site has to thread it. */
  if (typeof window !== "undefined") {
    const d = new URLSearchParams(window.location.search).get("demo");
    // Carry whichever way it was set — including an explicit "off", which must
    // not silently flip back on as the reader drills.
    if (d === "1" || d === "0") p.set("demo", d);
  }
  return p.toString();
}

/**
 * `name` is a display HINT, never an identifier. The page always prefers the
 * name the server returns; the hint only spares a deep-linked reader a
 * breadcrumb that reads "Member" until the dossier lands. A stale or absent
 * hint costs nothing.
 */
const withQuery = (path, range, member, name) => {
  const p = new URLSearchParams(filterQuery(range, member));
  if (name) p.set("name", String(name));
  const qs = p.toString();
  return qs ? `${path}?${qs}` : path;
};

export const personPath = (id, range, member, name) =>
  withQuery(`${drillBase()}/person/${encodeURIComponent(id)}`, range, member, name);

export const personTakeawayPath = (id, index, range, member, name) =>
  withQuery(`${drillBase()}/person/${encodeURIComponent(id)}/takeaway/${index}`, range, member, name);

export const personLogPath = (id, range, member, name) =>
  withQuery(`${drillBase()}/person/${encodeURIComponent(id)}/log`, range, member, name);

/**
 * A person's project, read from the PERSON's side. Same destination content as
 * projectMemberPath, but the trail reads Dashboard › Anand › Checkout rather
 * than Dashboard › Checkout › Anand — because the subject the reader drilled
 * from is the one that has to survive the descent.
 */
export const personProjectPath = (userId, projectId, range, member, name, projectName) => {
  const p = new URLSearchParams(filterQuery(range, member));
  if (name) p.set("name", String(name));
  if (projectName) p.set("pjn", String(projectName));
  const qs = p.toString();
  const base = `${drillBase()}/person/${encodeURIComponent(userId)}/project/${encodeURIComponent(projectId)}`;
  return qs ? `${base}?${qs}` : base;
};

export const projectPath = (id, range, member, name) =>
  withQuery(`${drillBase()}/project/${encodeURIComponent(id)}`, range, member, name);

/**
 * dayPath — one calendar day, opened from a rhythm bar.
 *
 * A day drill is the overview scoped to a single date: the page sets its own
 * range to [date, date] and re-reads /overview, so every panel on it is that
 * day and nothing else. The date is the route param; the window still rides in
 * the query string so a step further (into a person or project) inherits it.
 */
export const dayPath = (date, range, member) =>
  withQuery(`${drillBase()}/day/${encodeURIComponent(date)}`, range, member);

export const projectMemberPath = (projectId, userId, range, member, name) =>
  withQuery(`${drillBase()}/project/${encodeURIComponent(projectId)}/member/${encodeURIComponent(userId)}`, range, member, name);

/**
 * Tools carry their identity in the QUERY, not the path: a tool name is a raw
 * domain or window title ("github.com", "Visual Studio Code — src/app.tsx"),
 * and slashes in a path segment survive encoding badly across routers and
 * proxies. The query string has no such problem.
 */
function toolQuery(tool, range, member) {
  const p = new URLSearchParams(filterQuery(range, member));
  if (tool?.name) p.set("name", String(tool.name));
  if (tool?.type) p.set("type", String(tool.type));
  if (tool?.category) p.set("category", String(tool.category));
  return p;
}

export function toolPath(tool, range, member) {
  const qs = toolQuery(tool, range, member).toString();
  return qs ? `${drillBase()}/tool?${qs}` : `${drillBase()}/tool`;
}

/**
 * Drilling STAYS ON TOPIC. From a tool, a person means "this person INSIDE this
 * tool" — not their whole week, which is a different subject entirely. The tool
 * therefore travels with the person id, and so does the stretch below it, so
 * the trail reads Dashboard › Chrome › Anand › that stretch the whole way down.
 */
export function toolPersonPath(tool, userId, range, member, personName, day, sess) {
  const p = toolQuery(tool, range, member);
  p.set("u", String(userId));
  if (personName) p.set("pn", String(personName));
  if (day) p.set("day", String(day));
  if (sess) p.set("sess", String(sess));
  return `${drillBase()}/tool/person?${p.toString()}`;
}

export function toolStretchPath(tool, index, range, member, userId, personName) {
  const p = toolQuery(tool, range, member);
  p.set("i", String(index));
  /* Carried for the breadcrumb only: the index is always into the tool's full
     stretch list, so it stays valid whichever list the reader clicked from. */
  if (userId) p.set("u", String(userId));
  if (personName) p.set("pn", String(personName));
  return `${drillBase()}/tool/stretch?${p.toString()}`;
}

/** Today-only org glimpse — graphical, admin, on demand. */
export function alertSel(row) {
  const s = `${row?.kind || ""}|${row?.title || ""}|${row?.names || ""}`;
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return `${row?.kind === "blocker" ? "b" : "w"}${Math.abs(h).toString(36)}`;
}

export function alertPath(row, range, member) {
  const p = new URLSearchParams(filterQuery(range, member));
  p.set("tab", row?.kind === "blocker" ? "blocker" : "work");
  if (row) p.set("sel", alertSel(row));
  return `${drillBase()}/alerts?${p.toString()}`;
}

export const orgTodayPath = () => `${GRAPHICAL_BASE}/today`;

/** The written organization brief — "Know your organization". */
export function briefPath(range, member) {
  const qs = filterQuery(range, member);
  return qs ? `${CLASSIC_BASE}/brief?${qs}` : `${CLASSIC_BASE}/brief`;
}

/** Back to the dashboard the reader drilled from, filters intact. */
export const classicPath = (range, member) => {
  const home = drillBase();               // graphical home when in graphical, classic otherwise
  const qs = filterQuery(range, member);
  return qs ? `${home}?${qs}` : home;
};

/** One link for either subject kind — what onDrill({kind,id}) resolves to. */
export function subjectPath(subject, range, member) {
  if (!subject?.id) return drillBase();
  if (subject.kind === "day") return dayPath(subject.id, range, member);
  return subject.kind === "project"
    ? projectPath(subject.id, range, member, subject.name)
    : personPath(subject.id, range, member, subject.name);
}

/**
 * The window + member scope a drill-down page is being read under.
 *
 * Memoised on the raw query strings: every consumer feeds this straight into
 * useDrilldownData, whose effects key on the range object. A fresh object per
 * render would refetch forever.
 */
export function useClassicFilters() {
  const [params] = useSearchParams();
  const start = params.get("start");
  const end = params.get("end");
  const preset = params.get("preset");
  const member = params.get("member");

  const range = React.useMemo(() => {
    if (!start || !end) return rangeForPreset(preset || "today");
    const s = new Date(`${start}T00:00:00`);
    const e = new Date(`${end}T00:00:00`);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return rangeForPreset("today");
    s.setHours(0, 0, 0, 0);
    e.setHours(23, 59, 59, 999);
    return { start: s, end: e, key: preset || "custom" };
  }, [start, end, preset]);

  return { range, member: member || null };
}

export default CLASSIC_BASE;
